<template>
    <div class="competitiveMetrics">
        <div class="is-clearfix">
            <div class="chart-title is-pulled-left">
                <h1 class="title">{{title}}</h1>
                <h2 class="subtitle">{{selectedMetric ? `${selectedMetric.platform} ${selectedMetric.metric}: ` : ''}}{{subtitle}}</h2>
            </div>

            <div class="is-pulled-right level">
                <b-field class="is-middle" style="margin:0">
                    <b-radio-button size="is-small" v-model="showRawMetrics" native-value=''>
                        Normalised
                    </b-radio-button>
                    <b-radio-button size="is-small" v-model="showRawMetrics" native-value="true">
                        Raw Values
                    </b-radio-button>
                </b-field>

                <b-select class="metrics" size="is-small" v-model="selectedMetric">
                    <optgroup label="Conversation">
                        <option v-for="metric in availableMetrics.X" :value="metric" :key="metric.id">
                            {{ metric.platform }} - {{ metric.metric }}
                        </option>
                    </optgroup>
                    <optgroup label="Quality">
                        <option v-for="metric in availableMetrics.Y" :value="metric" :key="metric.id">
                            {{ metric.platform }} - {{ metric.metric }}
                        </option>
                    </optgroup>
                    <optgroup label="Community">
                        <option v-for="metric in availableMetrics.Z" :value="metric" :key="metric.id">
                            {{ metric.platform }} - {{ metric.metric }}
                        </option>
                    </optgroup>
                </b-select>

                <button class="button is-small" @click="exportPPTX" :disabled="isExporting || !project.metrics.length">
                    <b-icon icon="file-powerpoint"
                            size="is-small"
                            pack="far">
                    </b-icon>
                    <span>Export</span>
                </button>
            </div>

        </div>
        <hr>
        <chart class="chart-content" :chartData="chartData"/>
        <chart-legend :legends="chartData.settings.series"/>
        <div style="clear: both"></div>
    </div>
</template>

<script>
import Chart from '@/components/charts/Chart.vue';
import ChartLegend from '@/components/charts/ChartLegend.vue';

import _ from 'lodash';

import { exporterService } from '@/services/exporter-service';
import { chartTypes } from '@/components/charts/common/chartTypes';
import { chartLabelGeneratorService } from '@/services/chart-label-generator-service';
import { progressChartHelperService } from '@/services/progress-chart-helper-service';
import DefaultMetricWeightService from '@/services/default-metric-weight-service';
import { competitiveMetricRequestor } from '@/requestors/competitive-metric-requestor.js';

export default {
    name: 'CompetativeMetric',
    components: {
        Chart,
        ChartLegend
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            title: 'Competitive Metric Trend',
            subtitle: '',
            chartData: {
                chartType: chartTypes.ORDINALLINE,
                settings: {
                    xAxis: {
                        label: 'Date',
                        noOverlap: true
                    },
                    yAxis: {
                        minValue: 0,
                        label: 'Normalised & Weighted Metric Value',
                        ticks: 5
                    },
                    series: []
                },
                meta: {
                    metric: ['date', 'metricValue'],
                    series: 'columnId'
                },
                data: []
            },
            availableMetrics: [],
            selectedMetric: '',
            isExporting: false,
            showRawMetrics: ''
        };
    },
    watch: {
        'project.metrics': function (newMetrics) {
            this.chartData.data = generateDataMetrics(newMetrics, this.metricType, this.selectedMetric.sysMetric, this.chartData.settings.series);
            const latestMetric = _.maxBy(newMetrics, m => m.day);
            const earliestMetric = _.minBy(newMetrics, m => m.day);
            if (earliestMetric && latestMetric) {
                let strPattern = chartLabelGeneratorService.generateProgressChartLabelPattern(this.project.meta.period, this.project.meta.daySmoothing);
                this.subtitle = chartLabelGeneratorService.formatString(strPattern, earliestMetric.periodStartDate, latestMetric.periodEndDate, this.project.meta.daySmoothing);
            }
            this.chartData.settings.xAxis.label = progressChartHelperService.buildXAxisTitle(this.project.meta.period, this.project.meta.daySmoothing);
        },
        'project.settings.columns': {
            handler: function (newColumns) {
                this.chartData.settings.series = _(newColumns)
                    .filter(column => column.enabled)
                    .map(column => ({
                        id: column.id,
                        name: column.name,
                        colour: column.colour,
                        visible: true
                    }))
                    .value();

                this.chartData.data = generateDataMetrics(this.project.metrics, this.metricType, this.selectedMetric.sysMetric, this.chartData.settings.series);
            },
            deep: true
        },
        selectedMetric: function () {
            this.chartData.data = generateDataMetrics(this.project.metrics, this.metricType, this.selectedMetric.sysMetric, this.chartData.settings.series);
            this.chartData.settings.yAxis.label = `${this.selectedMetric.platform} ${this.selectedMetric.metric}`;
        },
        showRawMetrics: function () {
            this.chartData.data = generateDataMetrics(this.project.metrics, this.metricType, this.selectedMetric.sysMetric, this.chartData.settings.series);
        }
    },
    methods: {
        async exportPPTX () {
            this.isExporting = true;
            let pptxTitle = `${this.title} ${this.showRawMetrics ? ' (Raw Values)' : ' (Normalised Values)'}`;
            let pptxSubtitle = `${this.selectedMetric.platform} ${this.selectedMetric.metric}: ${this.subtitle}`;
            let pptxRequest = competitiveMetricRequestor.generatePPTXRequest(this.chartData.data, pptxTitle, pptxSubtitle, this.chartData.settings);

            try {
                await exporterService.exportPPTX(pptxRequest);
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Failed to export Competitive Metric Chart',
                    type: 'is-danger'
                });
            } finally {
                this.isExporting = false;
            }
        }
    },
    mounted () {
        let chartDetail = {
            getSlideRequest: () => {
                let pptxTitle = `${this.title} ${this.showRawMetrics ? ' (Raw Values)' : ' (Normalised Values)'}`;
                let pptxSubtitle = `${this.selectedMetric.platform} ${this.selectedMetric.metric}: ${this.subtitle}`;
                return competitiveMetricRequestor.getSlideRequest(this.chartData.data, pptxTitle, pptxSubtitle, this.chartData.settings);
            }
        };
        this.$emit('on-chart-mount-complete', chartDetail);
    },
    created () {
        // TODO: Consider whether metrics can be retrieved without weight information
        DefaultMetricWeightService.getDefaultMetricWeights()
            .then(response => {
                this.availableMetrics = _.groupBy(response.data, 'axis');

                this.selectedMetric = response.data[0];
            })
            .catch(() => {
                console.log('Failed to get available metrics');
            });
    },
    computed: {
        metricType () {
            return this.showRawMetrics ? 'brandMetrics' : 'normedBrandMetrics';
        }
    }
};

const generateDataMetrics = (projectMetrics, metricType, selectedMetric, series) => {
    return projectMetrics.map(m => ({
        columnId: m.columnId,
        date: m.periodName,
        metricValue: m[metricType][selectedMetric]
    }));
};

</script>

<style lang="scss" scoped>
    .metrics {
        max-width: 205px;
    }
    .chart-content {
        width: 100%;
        height: 300px;
    }
    .title {
        font-size: 16px;
    }
    .subtitle {
        font-size: 14px;
        margin-bottom: 5px;
    }
    hr {
        margin: 5px;
    }
</style>
